import React, { useState, useRef, useEffect, isValidElement } from 'react';
import { Project, Language } from '../types';
import { LOGO_BASE_URL } from '../constants';
import Card from './ui/Card';
import Button from './ui/Button';

interface ProjectsModalProps {
  isOpen: boolean;
  onClose: () => void;
  isLoggedIn: boolean;
  onLoginRequest: () => void;
  projects: Project[];
  onPartnershipRequest: (project: Project) => void;
  language: Language;
  t: {
    title: string;
    partnershipAvailable: string;
    confidentialDetails: string;
    progress: string;
    confidentialInfoTitle: string;
    confidentialInfoText: string;
    loginButton: string;
  }
}

interface ProjectCardProps {
  project: Project;
  isLoggedIn: boolean;
  onLoginRequest: () => void;
  onPartnershipRequest: (project: Project) => void;
  language: Language;
  t: ProjectsModalProps['t'];
}

import { Box } from 'lucide-react';

const SafeImage: React.FC<{ src?: string; alt: string; className?: string; icon?: React.ReactNode }> = ({ src, alt, className, icon }) => {
  const [error, setError] = useState(false);

  if (error || (!src && !icon)) {
    return (
      <div className={`${className} flex items-center justify-center bg-slate-800 rounded-lg text-slate-500`}>
        <Box className="w-1/2 h-1/2" />
      </div>
    );
  }

  if (icon && !src) {
    return <div className={className}>{icon}</div>;
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={() => setError(true)}
    />
  );
};

const LockClosedIcon = ({ className }: { className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 00-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
    </svg>
);

const ProjectCard: React.FC<ProjectCardProps> = ({ project, isLoggedIn, onLoginRequest, onPartnershipRequest, language, t }) => {
  const { name, city, area, icon, publicDescription, privateDetails, partnershipAvailable } = project;
  
  const [isLogoHovered, setLogoHovered] = useState(false);
  const leaveTimeoutRef = useRef<number | null>(null);

  const handleLogoMouseEnter = () => {
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }
    setLogoHovered(true);
  };

  const handleLogoMouseLeave = () => {
    leaveTimeoutRef.current = window.setTimeout(() => {
      setLogoHovered(false);
    }, 2000);
  };
  
  useEffect(() => {
    return () => {
      if (leaveTimeoutRef.current) {
        clearTimeout(leaveTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Card className="flex flex-col h-full relative overflow-hidden">
      {partnershipAvailable && (
        <button
          onClick={() => onPartnershipRequest(project)}
          className="absolute top-4 right-4 z-20 group"
          aria-label={`${t.partnershipAvailable} for ${name}`}
        >
            <div className="flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-500/20 text-green-300 ring-1 ring-inset ring-green-500/30 transition-all duration-300 group-hover:bg-green-500/30 group-hover:ring-green-400/50 group-hover:shadow-lg group-hover:shadow-green-500/20">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 mr-1.5">
                  <path fillRule="evenodd" d="M10.868 2.884c.321-.662 1.215-.662 1.536 0l1.681 3.46c.154.316.46.527.802.571l3.822.555c.734.107 1.028.986.494 1.493l-2.766 2.697a.913.913 0 00-.263.803l.652 3.805c.125.728-.642 1.285-1.303.938l-3.41-1.792a.913.913 0 00-.854 0l-3.41 1.792c-.66.347-1.428-.21-1.303-.938l.652-3.805a.913.913 0 00-.263-.803L2.332 8.963c-.534-.507-.24-1.386.494-1.493l3.822-.555a.913.913 0 00.802-.571l1.681-3.46z" clipRule="evenodd" />
                </svg>
                {t.partnershipAvailable}
            </div>
        </button>
      )}
      <div
        onMouseEnter={handleLogoMouseEnter}
        onMouseLeave={handleLogoMouseLeave}
        className={`absolute transition-all duration-500 ease-in-out z-10 cursor-pointer overflow-hidden
          ${isLogoHovered
            ? 'inset-0 bg-slate-900/80 backdrop-blur-md'
            : 'top-6 left-6 w-8 h-8 bg-transparent'
          }`
        }
      >
        <SafeImage 
          src={project.iconUrl} 
          icon={icon}
          alt={`Logo de ${name}`} 
          className={`object-contain transition-all duration-500 ease-in-out w-full h-full ${isLogoHovered ? 'p-8 scale-110' : ''}`}
        />
      </div>
      
      <div className={`transition-opacity duration-300 ${isLogoHovered ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
        <div className="flex items-start justify-between mb-4">
            <div className="flex items-center">
                <div className="w-8 h-8 flex-shrink-0" aria-hidden="true" />
                <div className="ml-4">
                    <h3 className="text-xl font-bold text-white">{name}</h3>
                    <p className="text-sm text-slate-400">
                      {typeof city === 'string' ? city : ((city as any)?.[language] || (city as any)?.['es'] || '')} - {typeof area === 'string' ? area : ((area as any)?.[language] || (area as any)?.['es'] || '')}
                    </p>
                </div>
            </div>
        </div>
        <p className="text-slate-300 mb-6 flex-grow">{typeof publicDescription === 'string' ? publicDescription : ((publicDescription as any)?.[language] || (publicDescription as any)?.['es'] || '')}</p>

        {isLoggedIn ? (
          <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700">
            <h4 className="font-bold text-green-400 mb-3">{t.confidentialDetails}</h4>
            <div className="mb-3">
              <div className="flex justify-between items-center mb-1 text-sm text-slate-300">
                <span>{t.progress}</span>
                <span>{privateDetails.progress}%</span>
              </div>
              <div className="w-full bg-slate-700 rounded-full h-2.5">
                <div className="bg-green-500 h-2.5 rounded-full" style={{ width: `${privateDetails.progress}%` }}></div>
              </div>
            </div>
            <div className="space-y-2 text-sm">
              {privateDetails?.kpis?.map((kpi: any) => (
                <div key={kpi?.label?.es || kpi?.label} className="flex justify-between text-slate-300">
                  <span>{typeof kpi?.label === 'string' ? kpi.label : ((kpi?.label as any)?.[language] || (kpi?.label as any)?.['es'] || '')}:</span>
                  <span className="font-semibold text-white">{kpi?.value}</span>
                </div>
              ))}
            </div>
            <p className="mt-4 text-xs text-slate-400 italic">{typeof privateDetails?.confidentialInfo === 'string' ? privateDetails.confidentialInfo : ((privateDetails?.confidentialInfo as any)?.[language] || (privateDetails?.confidentialInfo as any)?.['es'] || '')}</p>
          </div>
        ) : (
          <div className="text-center p-6 bg-slate-900/50 rounded-lg border border-dashed border-slate-600">
            <LockClosedIcon className="w-8 h-8 mx-auto text-slate-500 mb-2"/>
            <h4 className="font-bold text-slate-300">{t.confidentialInfoTitle}</h4>
            <p className="text-sm text-slate-400 mb-4">{t.confidentialInfoText}</p>
            <Button variant="primary" onClick={onLoginRequest} className="text-sm px-4 py-2 w-full">{t.loginButton}</Button>
          </div>
        )}
      </div>
    </Card>
  );
};

const ProjectsModal = ({ isOpen, onClose, isLoggedIn, onLoginRequest, projects, onPartnershipRequest, language, t }: ProjectsModalProps) => {
  if (!isOpen) return null;

  const activeProjects = projects.filter(p => !p.isPaused).map(project => {
    // Normalize iconUrl if it's a relative path (doesn't start with http/https)
    let iconUrl = project.iconUrl;
    if (iconUrl && !iconUrl.startsWith('http')) {
      iconUrl = `${LOGO_BASE_URL}${iconUrl}`;
    }
    return { ...project, iconUrl };
  });

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6 border-b border-slate-800 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-white">{t.title}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">&times;</button>
        </div>
        <div className="p-6 overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {activeProjects.map((project) => (
              <ProjectCard key={project.id} project={project} isLoggedIn={isLoggedIn} onLoginRequest={onLoginRequest} onPartnershipRequest={onPartnershipRequest} language={language} t={t} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectsModal;